import React, { useState, useEffect } from "react";

const ElectricityBillEstimator = () => {
  const [unitsConsumed, setUnitsConsumed] = useState(250);
  const [ratePerUnit, setRatePerUnit] = useState(6);
  const [fixedCharge, setFixedCharge] = useState(100);
  const [taxRate, setTaxRate] = useState(5);
  const [result, setResult] = useState({
    energyCharges: "0.00",
    fixedCharges: "0.00",
    subtotal: "0.00",
    taxAmount: "0.00",
    totalBill: "0.00",
  });

  // Electricity Bill Calculation Function
  const calculateBill = () => {
    const units = parseFloat(unitsConsumed);
    const rate = parseFloat(ratePerUnit);
    const fixed = parseFloat(fixedCharge);
    const tax = parseFloat(taxRate) / 100;

    if (isNaN(units) || isNaN(rate) || isNaN(fixed) || isNaN(tax) || units < 0 || rate < 0 || fixed < 0) return;

    // Calculate energy charges
    const energyCharges = units * rate;
    
    // Calculate subtotal
    const subtotal = energyCharges + fixed;
    
    // Calculate tax
    const taxAmount = subtotal * tax;
    
    // Calculate total bill
    const totalBill = subtotal + taxAmount;

    setResult({
      energyCharges: energyCharges.toFixed(2),
      fixedCharges: fixed.toFixed(2),
      subtotal: subtotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      totalBill: totalBill.toFixed(2),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
  calculateBill();
}, [
  unitsConsumed,
  ratePerUnit,
  fixedCharge,
  taxRate
]);

  const energyPercentage = result.totalBill > 0 
    ? ((Number(result.energyCharges) / Number(result.totalBill)) * 100).toFixed(1)
    : 0;

  const fixedPercentage = result.totalBill > 0 
    ? ((Number(result.fixedCharges) / Number(result.totalBill)) * 100).toFixed(1)
    : 0;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
            </svg>
          </div>
          <h1>Electricity Bill Calculator</h1>
          <p>
            Calculate your monthly electricity bill based on units consumed, rate per unit, fixed charges, and applicable taxes
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                </svg>
                Bill Details
              </h2>

              {/* Units Consumed */}
              <div className="input-group">
                <label>Units Consumed (kWh)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={unitsConsumed}
                    onChange={(e) => setUnitsConsumed(e.target.value)}
                    className="calc-input"
                    placeholder="250"
                  />
                </div>
                <div className="quick-select-group">
                  {[100, 200, 300, 500].map((units) => (
                    <button
                      key={units}
                      onClick={() => setUnitsConsumed(units)}
                      className="quick-select-btn primary"
                    >
                      {units} kWh
                    </button>
                  ))}
                </div>
              </div>

              {/* Rate per Unit */}
              <div className="input-group">
                <label>Rate per Unit (₹)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={ratePerUnit}
                    onChange={(e) => setRatePerUnit(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                </div>
                <div className="quick-select-group">
                  {[4, 5, 6, 8].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setRatePerUnit(rate)}
                      className="quick-select-btn secondary"
                    >
                      ₹{rate}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fixed Charge */}
              <div className="input-group">
                <label>Fixed Charge (₹)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={fixedCharge}
                    onChange={(e) => setFixedCharge(e.target.value)}
                    className="calc-input"
                    placeholder="100"
                  />
                </div>
                <div className="quick-select-group">
                  {[50, 100, 150, 200].map((charge) => (
                    <button
                      key={charge}
                      onClick={() => setFixedCharge(charge)}
                      className="quick-select-btn success"
                    >
                      ₹{charge}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tax Rate */}
              <div className="input-group">
                <label>Tax Rate (%)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="calc-input"
                    placeholder="5"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculateBill} className="calculate-btn">
                Estimate Bill
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Bill Summary
              </h2>

              <div className="result-card">
                {/* Energy Charges */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Energy Charges</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.energyCharges).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {unitsConsumed} kWh × ₹{ratePerUnit}
                  </div>
                </div>

                {/* Fixed Charges */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Fixed Charges</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.fixedCharges).toLocaleString("en-IN")}
                  </div>
                </div>

                {/* Tax Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Tax Amount</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.taxAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {taxRate}% tax on subtotal
                  </div>
                </div>

                {/* Total Bill - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Bill Amount</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalBill).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Estimated monthly bill
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Bill Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Energy Charges</span>
                      <span className="breakdown-percentage">
                        {energyPercentage}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${energyPercentage}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Fixed Charges</span>
                      <span className="breakdown-percentage">
                        {fixedPercentage}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${fixedPercentage}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Tax</span>
                      <span className="breakdown-percentage">
                        {((Number(result.taxAmount) / Number(result.totalBill)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill purple"
                        style={{
                          width: `${(Number(result.taxAmount) / Number(result.totalBill)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Electricity Bill Calculator Work?</h3>
              <p>
                The electricity bill calculator estimates your monthly electricity bill by multiplying 
                the units consumed (in kWh) by the rate per unit charged by your electricity provider. 
                It then adds fixed charges (meter rent, service charges) and calculates applicable taxes 
                to give you the total payable amount. This helps you budget and monitor your electricity usage.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              Total Bill = (Units × Rate) + Fixed Charges + Tax
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Units</div>
                <div className="formula-param-value">Energy consumed in kWh</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Rate</div>
                <div className="formula-param-value">Cost per unit (₹/kWh)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Fixed Charges</div>
                <div className="formula-param-value">Monthly service charges</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Tax</div>
                <div className="formula-param-value">Applicable tax percentage</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is a unit of electricity?</h5>
              <p>
                A unit of electricity, also called a kilowatt-hour (kWh), is the amount of energy 
                consumed when a 1000-watt appliance runs for one hour. For example, a 100-watt bulb 
                running for 10 hours consumes 1 kWh (1 unit). Your electricity meter measures total 
                consumption in units, which is the basis for your monthly bill calculation.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are fixed charges in electricity bills?</h5>
              <p>
                Fixed charges are mandatory monthly fees charged by electricity providers regardless 
                of consumption. These include meter rent, service charges, and infrastructure maintenance 
                costs. Fixed charges vary by state, provider, and connection type (residential, commercial). 
                They typically range from ₹20 to ₹200 per month depending on your sanctioned load.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I reduce my electricity bill?</h5>
              <p>
                Switch to LED bulbs, use energy-efficient appliances (5-star rated), unplug devices 
                when not in use, optimize AC usage (set to 24-25°C), use natural light during daytime, 
                regularly service appliances, avoid standby mode on electronics, and consider solar 
                panels for long-term savings. These steps can reduce consumption by 20-40%.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the electricity tariff structure in India?</h5>
              <p>
                Most Indian states follow a slab-based tariff system where rates increase with consumption. 
                For example: 0-100 units at ₹3/unit, 101-300 units at ₹5/unit, and above 300 units at 
                ₹7/unit. Some states offer subsidies for low consumption. Commercial rates are higher 
                than residential. Check your state electricity board website for specific tariffs.
              </p>
            </div>

            <div className="faq-item">
              <h5>What taxes are applicable on electricity bills?</h5>
              <p>
                Electricity bills typically include electricity duty (a state tax varying from 0-15%), 
                and some states charge additional taxes like municipal tax or wheeling charges. From 
                July 2017, GST is not applicable on electricity for domestic consumption. However, 
                charges for meters, connections, and additional services may attract GST at 18%.
              </p>
            </div>

            <div className="faq-item">
              <h5>How accurate is the electricity bill calculator?</h5>
              <p>
                The calculator provides estimates based on your inputs. Actual bills may vary slightly 
                due to factors like slab-based pricing, peak/off-peak hours, power factor penalties, 
                meter reading dates, and seasonal adjustments. For precise calculations, check your 
                state electricity board's tariff order. The calculator is best used for budgeting and 
                consumption tracking purposes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElectricityBillEstimator;